import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ItemSelection from '../../OldRydercup/pages/ItemSelection';
import '../styles/ItemSelectionPage.css';

const ItemSelectionPage = ({ cart, updateCart, products }) => {
  const navigate = useNavigate();
  const [currentSegment, setCurrentSegment] = useState(1);
  const [availableItems, setAvailableItems] = useState({
    tier1: [],
    tier2: []
  });

  // Reset if no bundle selected
  useEffect(() => {
    if (!cart.bundle) {
      navigate('/bundles');
    }
  }, [cart.bundle, navigate]);

  // Filter available items based on current segment and previous selections
  useEffect(() => {
    const allSelectedItems = [
      ...(cart.segment1?.items || []),
      ...(cart.segment2?.items || []),
      ...(cart.segment3?.items || [])
    ].filter(Boolean);

    const filteredTier1 = products.additionalItems.tier1.filter(
      item => !allSelectedItems.some(selected => selected.id === item.id)
    );
    
    const filteredTier2 = products.additionalItems.tier2.filter(
      item => !allSelectedItems.some(selected => selected.id === item.id)
    );

    setAvailableItems({
      tier1: filteredTier1,
      tier2: filteredTier2
    });
  }, [cart, products]);

  const getCurrentSegmentItems = () => {
    return cart[`segment${currentSegment}`]?.items || [];
  };

  const isSegmentComplete = () => {
    const currentItems = getCurrentSegmentItems();
    const hasTwoTier1 = currentItems.filter(item => item.tier === 'tier1').length >= 2;
    const hasOneTier2 = currentItems.some(item => item.tier === 'tier2');
    return hasTwoTier1 || hasOneTier2;
  };

  const handleItemSelect = (item) => {
    const updatedCart = { ...cart };
    const currentSegmentKey = `segment${currentSegment}`;
    
    // Initialize segment if it doesn't exist
    if (!updatedCart[currentSegmentKey]) {
      updatedCart[currentSegmentKey] = { items: [] };
    }

    // Add the new item
    updatedCart[currentSegmentKey].items = [
      ...updatedCart[currentSegmentKey].items,
      item
    ];

    updateCart(updatedCart);

    // If tier2 item was selected or we have 2 tier1 items, move to next segment
    if (item.tier === 'tier2' || 
        updatedCart[currentSegmentKey].items.filter(i => i.tier === 'tier1').length >= 2) {
      if (currentSegment < 3) {
        setCurrentSegment(currentSegment + 1);
      }
    }
  };

  const handleContinue = () => {
    if (isSegmentComplete()) {
      if (currentSegment < 3) {
        setCurrentSegment(currentSegment + 1);
      } else {
        navigate('/confirmation');
      }
    }
  };

  const getMaxSelections = () => {
    const currentItems = getCurrentSegmentItems();
    const hasTier2 = currentItems.some(item => item.tier === 'tier2');
    
    // If already has a tier2 item, no more selections allowed
    if (hasTier2) return 1;
    
    // Otherwise can select up to 2 tier1 items
    return 2 - currentItems.filter(item => item.tier === 'tier1').length;
  };

  return (
    <div className="item-selection-page">
      <h2>Additional Items Selection (Segment {currentSegment}/3)</h2>
      
      <div className="progress-indicator">
        {[1, 2, 3].map((i) => (
          <div 
            key={i}
            className={`step ${currentSegment === i ? 'active' : ''}`}
          >
            {i}
          </div>
        ))}
      </div>

      <div className="selection-instructions">
        {isSegmentComplete() ? (
          <p>Segment {currentSegment} complete! {currentSegment < 3 ? 'Continue to next segment.' : 'Ready to review order.'}</p>
        ) : (
          <p>Select up to 2 Standard items OR 1 Premium item</p>
        )}
      </div>

      <div className="tier-container">
        {!isSegmentComplete() && (
          <>
            <ItemSelection
              tier="tier1"
              items={availableItems.tier1}
              selectedItems={getCurrentSegmentItems()}
              onItemSelect={handleItemSelect}
              maxSelections={getMaxSelections()}
            />
            
            <ItemSelection
              tier="tier2"
              items={availableItems.tier2}
              selectedItems={getCurrentSegmentItems()}
              onItemSelect={handleItemSelect}
              maxSelections={getMaxSelections()}
            />
          </>
        )}
      </div>

      <div className="navigation-buttons">
        <button 
          className="back-button"
          onClick={() => currentSegment > 1 ? setCurrentSegment(currentSegment - 1) : navigate('/bundles')}
        >
          Back
        </button>
        
        {isSegmentComplete() && currentSegment < 3 && (
          <button
            className="continue-button"
            onClick={handleContinue}
          >
            Continue to Segment {currentSegment + 1}
          </button>
        )}
        
        {isSegmentComplete() && currentSegment === 3 && (
          <button
            className="continue-button"
            onClick={handleContinue}
          >
            Review Order
          </button>
        )}
      </div>
    </div>
  );
};

export default ItemSelectionPage;